// src/pages/ForgotPinScreen.tsx — Phase 1 Slice B commit 3.
//
// POST /api/auth/forgot { email } per auth.md §5.3. The backend always
// responds 200 with { ok: true } (no enumeration) and emails a new PIN
// when the address matches a hub_users row. UI mirrors that: success is
// the same surface whether or not the address was registered.

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch, ApiError } from "../api";
import { C, F } from "../theme";
import {
  AuthShell,
  BackLink,
  BrandMark,
  Button,
  Card,
  Eyebrow,
  Field,
  SuccessCard,
} from "../components/ui";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type State =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "sent" }
  | { kind: "error"; message: string };

export function ForgotPinScreen() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [state, setState] = useState<State>({ kind: "idle" });

  const trimmed = email.trim();
  const validEmail = EMAIL_REGEX.test(trimmed);

  async function submit() {
    if (state.kind === "submitting") return;
    if (!validEmail) {
      setState({ kind: "error", message: "Enter a valid email address." });
      return;
    }
    setState({ kind: "submitting" });
    try {
      await apiFetch<{ ok: true }>("/api/auth/forgot", {
        method: "POST",
        body: JSON.stringify({ email: trimmed }),
      });
      setState({ kind: "sent" });
    } catch (e: unknown) {
      const msg = e instanceof ApiError ? e.message : "Couldn't reach the server. Try again.";
      setState({ kind: "error", message: msg });
    }
  }

  return (
    <AuthShell>
      <div style={containerStyle}>
        <BrandMark />
        <Eyebrow style={{ fontSize: 11, letterSpacing: 3, textTransform: "uppercase", fontWeight: 800 }}>
          Account recovery
        </Eyebrow>
        <h1 style={headerStyle}>Forgotten PIN</h1>
        <p style={subStyle}>Forgotten your PIN? We&apos;ll send a new one.</p>

        {state.kind === "sent" ? (
          <SuccessCard
            tone="brass"
            title="New PIN on the way"
            body="If that email is registered, a new PIN is on its way. Check your inbox."
            onPrimary={() => navigate("/login")}
          />
        ) : (
          <Card style={{ width: "100%", maxWidth: 380 }}>
            <Field
              label="Your personal email address"
              value={email}
              onChange={setEmail}
              type="email"
              ph="you@example.com"
              disabled={state.kind === "submitting"}
            />
            {state.kind === "error" && (
              <div role="alert" style={errorStyle}>
                {state.message}
              </div>
            )}
            <Button
              variant="primary"
              fullWidth
              onClick={submit}
              disabled={!validEmail || state.kind === "submitting"}
            >
              {state.kind === "submitting" ? "Sending…" : "Send new PIN"}
            </Button>
            <BackLink onClick={() => navigate("/login")} />
          </Card>
        )}
      </div>
    </AuthShell>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const containerStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 440,
  padding: "32px 4px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
};

const headerStyle: React.CSSProperties = {
  fontFamily: F.serif,
  fontSize: 28,
  fontWeight: 700,
  color: C.ink,
  margin: "4px 0 6px",
  letterSpacing: -0.6,
  textAlign: "center",
};

const subStyle: React.CSSProperties = {
  fontFamily: F.serif,
  fontStyle: "italic",
  fontSize: 14,
  color: C.inkSoft,
  marginTop: 0,
  marginBottom: 24,
  fontWeight: 600,
  textAlign: "center",
  maxWidth: 320,
};

const errorStyle: React.CSSProperties = {
  padding: "8px 14px",
  borderRadius: 10,
  background: C.signalBg,
  border: "1px solid rgba(184,58,43,0.22)",
  color: C.signalDk,
  fontFamily: F.body,
  fontSize: 13,
  fontWeight: 700,
  marginBottom: 12,
  textAlign: "center",
};
